// services.js

import { t } from "i18next";

// Helper function: Convert sessionStorage key/value pairs into custom headers.
// Only keys that are valid HTTP header names are forwarded — app-state keys
// like the per-event cart (`shopCart:<event_id>`, colon = invalid header name)
// stay local instead of blowing up fetch with "Invalid name".
const HEADER_NAME_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const getSessionHeaders = () => {
  return Object.fromEntries(
    Object.keys(sessionStorage)
      .filter((key) => HEADER_NAME_RE.test(key))
      .map(key => [key, sessionStorage.getItem(key)])
  );
};


/**
 * Generic API request function.
 * @param {string} method - The HTTP method ('GET', 'POST', etc.).
 * @param {string} url - The API endpoint (will be prefixed with /api).
 * @param {object|null} body - The request body (for non-GET requests).
 * @param {object} extraHeaders - Any additional headers.
 * @returns {Promise<object>} The JSON response.
 */
export const apiRequest = async (method, url, body = null, extraHeaders = {}) => {
  const fullUrl = `/api${url}`;
  const sessionHeaders = getSessionHeaders();

  // Combine headers via Headers so case-insensitive duplicates collapse.
  // (e.g. sessionStorage key `otherTeamId` vs. caller-supplied `Otherteamid`
  // would otherwise be sent twice and joined with ", " by the browser.)
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  for (const [k, v] of Object.entries(sessionHeaders)) {
    if (v == null) continue;
    // Session values are arbitrary app state; one unsendable value (e.g. an
    // emoji, invalid in a header) must not break every API call on the page.
    try { headers.set(k, v); } catch { /* skip unsendable session entries */ }
  }
  for (const [k, v] of Object.entries(extraHeaders)) if (v != null) headers.set(k, v);

  const options = {
    method: method.toUpperCase(),
    headers,
    credentials: 'include', // Send cookies (e.g., JWT)
  };

  if (body && method.toUpperCase() !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(fullUrl, options);

  // ✅ Redirect to login only on GET + 401 — and only for somebody who WAS signed in.
  //
  // ★ The marker is the whole point. This branch reads a 401 as "your session expired", which is
  // right for a page you reached by logging in and wrong for a public one: a signed-out visitor on
  // /public-event/<id> was being logged out and bounced to /login by a request they never made,
  // because one component on the page asks a @token_required endpoint. Nobody notices while
  // building it, because a developer is always signed in.
  //
  // A genuinely expired session still carries `isLoggedIn`, so it still logs out and still says
  // so. Without the marker the 401 falls through to the ordinary error path below and the caller
  // handles it like any other refusal.
  if (response.status === 401 && method.toUpperCase() === 'GET'
      && localStorage.getItem('isLoggedIn') === '1') {
    try {
      const errorData = await response.json();
      sessionStorage.setItem('toastMessage', errorData.error || 'sessionExpired');
    } catch {
      sessionStorage.setItem('toastMessage', 'Session expired');
    }
    await logout(); // Will redirect to /login
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let errorMessage = 'Something went wrong';
    let errorData = null;
    try {
      // Attempt to extract the error message from the response body. Routes are
      // inconsistent: some return {message}, others {error} — read either so the
      // caller gets the specific key (e.g. 'shopUnavailable') instead of a
      // generic fallback.
      errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (err) {
      // In case parsing fails, the generic error message will be used.
    }
    // Carry the whole body on the Error so callers that need extra fields (e.g.
    // `signed_in_as` on a blocked claim) can read them; message stays the key.
    const error = new Error(errorMessage);
    error.data = errorData;
    throw error;
  }
  return response.json();
};

/**
 * The line to show a person when an apiRequest failed.
 *
 * ★ The KEY first, the prose second. A route that refuses for a reason worth naming sends both —
 * `{'error': 'cannotCreate', 'message': "Pick the programme this event belongs to."}` — and
 * apiRequest puts the PROSE on `err.message`, where a screen doing `t(err.message)` can only
 * miss: an English sentence is not a translation key, so a precise 400 rendered as the generic
 * "Could not save". Read the key off the body, fall back to the message (routes that send only
 * that put a key in it), and only then to the caller's generic line.
 */
/**
 * POST a draft that MAY carry a picture — the shape every "name, a line about it, and optionally
 * an image" editor in the app needs (a theme, a value, a house).
 *
 * ★ `apiRequest` always sends JSON, so a file needs the multipart variant; everything else about
 * the request is identical, and writing the split out per editor is how three copies of it drift.
 *
 * ⚠️ Empty strings are INCLUDED in the form, nulls are not. A blank description is how a line is
 * taken back off; skipping it would make "clear the text and change the picture in one go"
 * silently keep the old text. A null is a field the draft never had, not one somebody emptied.
 */
export const saveWithOptionalFile = (path, draft, file) => {
  if (!file) return apiRequest('POST', path, draft);
  const form = new FormData();
  Object.entries(draft || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, v);
  });
  form.append('file', file);
  return apiFormRequest(path, form);
};

export const apiErrorText = (t, err, fallback = 'saveFailed') => {
  const key = (err && err.data && err.data.error) || (err && err.message);
  return key ? t(key, { defaultValue: t(fallback) }) : t(fallback);
};

/**
 * Ask the browser to download a same-origin API route.
 *
 * ★ An anchor, not a fetch: the route sets Content-Disposition, so the browser saves the file and
 * the user stays on the page, and the JWT rides on the cookie exactly as it does for every other
 * call. `CompanyEventPeople` has done this by hand for the attendee sheet since it shipped; the
 * print buttons are the fourth and fifth caller, which is one too many to keep copying.
 *
 * `params` are appended as a query string, blanks dropped.
 */
export const downloadApiFile = (path, params = {}) => {
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const a = document.createElement('a');
  a.href = `/api${path}${query ? `?${query}` : ''}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Special API function for submitting FormData.
 * Use this when you need to send form data (e.g., file uploads).
 */
export const apiFormRequest = async (url, formData, extraHeaders = {}) => {
  const fullUrl = `/api${url}`;
  const sessionHeaders = getSessionHeaders();
  // Don't set Content-Type here; let fetch set it automatically with FormData.
  // Use Headers so case-insensitive duplicates between sessionStorage keys
  // and caller-supplied headers don't end up joined with ", " on the wire.
  const headers = new Headers();
  for (const [k, v] of Object.entries(sessionHeaders)) {
    if (v == null) continue;
    try { headers.set(k, v); } catch { /* skip unsendable session entries */ }
  }
  for (const [k, v] of Object.entries(extraHeaders)) if (v != null) headers.set(k, v);

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    // Read the specific key/message off the body exactly like apiRequest does. A multipart
    // route rejects for reasons worth reading out loud ("that file is not an image we
    // accept"), and swallowing them all into one generic string hid every one of them.
    let errorMessage = 'Something went wrong';
    let errorData = null;
    try {
      errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (err) {
      // Not a JSON body — the generic message stands.
    }
    const error = new Error(errorMessage);
    error.data = errorData;
    throw error;
  }
  return response.json();
};


export const fetchUserInfo = async (url, setUserInfo, setLoading, setError) => {
  try {
    const data = await apiRequest('GET', url);
    setUserInfo(data);
    setLoading(false);
    // Every caller of fetchUserInfo is an authed-only page (public pages
    // call apiRequest directly). A successful 200 means a real session, so
    // sync the UI auth flag for anyone who was logged in before this code
    // shipped — saves us from force-logging them out.
    if (localStorage.getItem('isLoggedIn') !== '1') {
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
    }
  } catch (err) {
    setError(err.message);
    setLoading(false);
  }
};



// Intl throws a RangeError on an unknown zone, and Event.time_zone is free text
// out of the back office — a bad value must fall back, never blank the chip.
const withZone = (options, timeZone) => {
  if (!timeZone) return options;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone });
    return { ...options, timeZone };
  } catch {
    return options;
  }
};


// The zone an event falls back to when it carries none of its own — the same default as
// shared_models' `event_time.event_zone`, so the app and the back office read a row alike.
const EVENT_TZ_FALLBACK = 'Europe/Brussels';


// ★ Event datetimes arrive NAIVE: the API serialises a stored-UTC value and `isoformat()`
// writes no 'Z' to say so, so `new Date()` reads '2026-09-15T04:00:00' in the BROWSER's zone —
// which cancels the whole conversion out. Seen from Dubai, an 08:00 Lab came back as its own
// raw 04:00 UTC. Stamp the marker on before parsing anything about to be shown in a zone.
//
// ★ And they arrive in TWO shapes. A route that hands Flask a raw datetime (serialize_object
// does) gets the HTTP date jsonify writes for it — 'Mon, 28 Sep 2026 13:30:00 GMT' — which
// already names its zone. Stamping a 'Z' on THAT produced an Invalid Date, so the public event
// page read "Invalid date", lost its time chip and hid its countdown. Only the ISO form is
// missing the marker, so only the ISO form is given one.
export const parseEventDate = (value) => {
  const s = String(value);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(s) ? s : `${s}Z`);
};


// A link that opens the venue in Google Maps, from an event's `location_embed`.
//
// ★ That field holds the whole `<iframe …>` Google's "Embed a map" hands out (every event in
// the database does), not a URL. PublicEvent renders it as HTML; used as an `href` the browser
// read it as a relative path and the "How to get there" button 404-ed. The embed's own src is
// a map tile, not somewhere to navigate FROM, so the link searches the place the embed names
// (`!2s<name>`), else its centre (`!3d<lat>`/`!2d<lng>`). Null when there is nothing to open.
const mapsSearch = (query) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

export const mapsLink = (embed, placeName) => {
  const s = String(embed || '').trim();
  if (!s) return placeName ? mapsSearch(placeName) : null;
  const src = (s.match(/src\s*=\s*["']([^"']+)["']/i) || [])[1] || (/^https?:\/\//i.test(s) ? s : null);
  if (!src) return placeName ? mapsSearch(placeName) : null;
  const url = src.replace(/&amp;/g, '&');
  if (!/google\.[^/]+\/maps\/embed/i.test(url)) return url;
  const name = (url.match(/!2s([^!&]+)/) || [])[1];
  if (name) {
    try { return mapsSearch(decodeURIComponent(name.replace(/\+/g, ' '))); } catch (e) { /* fall through */ }
  }
  const lat = (url.match(/!3d(-?[\d.]+)/) || [])[1];
  const lng = (url.match(/!2d(-?[\d.]+)/) || [])[1];
  if (lat && lng) return mapsSearch(`${lat},${lng}`);
  return placeName ? mapsSearch(placeName) : url;
};


// The zone an event is rendered in: its own, else the shared fallback — never the viewer's.
export const eventZone = (event) => (event && event.time_zone) || EVENT_TZ_FALLBACK;


// `timeZone` is optional and only for EVENT datetimes: those are stored UTC, so
// an event has to be rendered in its own zone (Event.time_zone) rather than the
// viewer's — a Dubai Lab reads 08:00 from Brussels too. Passing a zone is also what
// declares the value UTC, so the stamp is read as UTC instead of browser-local.
// Left out, this behaves exactly as before (browser-local on both counts).
export const formatDate = (dateString, timeZone) => {
  if (!dateString) return 'Invalid date';

  const date = timeZone ? parseEventDate(dateString) : new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid date';

  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', withZone(options, timeZone)).format(date);
};


// Wall-clock 'HH:MM' for a datetime, in `timeZone` when given (see formatDate).
export const formatTime = (value, timeZone) => {
  if (!value) return '';

  const date = timeZone ? parseEventDate(value) : new Date(value);
  if (isNaN(date.getTime())) return '';

  const options = { hour: '2-digit', minute: '2-digit', hour12: false };
  return new Intl.DateTimeFormat('en-GB', withZone(options, timeZone)).format(date);
};


// 'HH:MM' straight out of a raw datetime string. Deliberately not via Date(), which
// would apply the BROWSER's zone before we have decided which zone to read it in.
// The clock is read out of either shape the API sends (see parseEventDate): the 'T' of an ISO
// stamp, or the space before the time in an HTTP date.
const rawHHMM = (value) => {
  const m = String(value || '').match(/(?:T|\s)(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};


// Does this event datetime carry a real time, or is it a date-only entry?
// ★ Tested on the RAW stored value. `date`/`end_date` are UTC, and converting a date-only
// midnight first hands it a fake wall time from the offset (04:00 in Dubai) — which would
// put a start time on an event that has none.
export const hasEventTime = (value) => {
  const hhmm = rawHHMM(value);
  return !!hhmm && hhmm !== '00:00';
};


// The event's day, read in its OWN zone when it carries a time. A date-only event renders
// off UTC instead, so the stored calendar day survives a negative offset.
export const eventDayLabel = (event) => (
  event && event.date
    ? formatDate(event.date, hasEventTime(event.date) ? eventZone(event) : 'UTC')
    : null
);


// The event's calendar day as a sortable/comparable 'YYYY-MM-DD' key, read in exactly the zone
// `eventDayLabel` prints it in — its own when it carries a time, UTC when it is date-only.
//
// ★ Deliberately not `event.date.slice(0, 10)`. That is the raw UTC day, and a Dubai Lab at
// 01:00 local is the PREVIOUS day in UTC — so a date filter built on the raw string would drop
// an event out of the very range whose dates the row is printing.
export const eventDayKey = (event) => {
  if (!event || !event.date) return null;
  const parsed = parseEventDate(event.date);
  if (isNaN(parsed.getTime())) return null;
  const zone = hasEventTime(event.date) ? eventZone(event) : 'UTC';
  // 'en-CA' is ISO order, so no re-assembly of the parts is needed.
  return new Intl.DateTimeFormat('en-CA', withZone(
    { year: 'numeric', month: '2-digit', day: '2-digit' }, zone)).format(parsed);
};


// The event's start time in its own zone ('08:00'), or '' when it is a date-only entry.
export const eventStartLabel = (event) => (
  event && hasEventTime(event.date) ? formatTime(event.date, eventZone(event)) : ''
);


// 'HH:MM' read LITERALLY out of a datetime string, with no zone conversion at all.
//
// ★ The company-event API sends WALL-CLOCK stamps ('2026-09-04T18:00') that have already been
// converted into the event's own timezone server-side. Reading one back through Date() would
// re-interpret it in the BROWSER's zone, so an 18:00 Dubai townhall would read 15:00 to a
// colleague in Brussels — the exact opposite of what the conversion was for. Use this, and
// `shortDate` for the day, on anything that arrives as a wall-clock stamp.
export const wallClockTime = (value) => rawHHMM(value);


// Short, language-aware day for a plain ISO date ("2026-09-30"). Parsed at LOCAL
// midnight so the day never slips back one west of UTC (a bare ISO date is UTC).
// Returns null on a missing/unparseable value, so callers can just skip the line.
export const shortDate = (iso, lang) => {
  if (!iso) return null;
  const date = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString(lang || undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};


// What a prize LOOKS like, decided in one place for the three screens that draw one.
//
// ★ Three sources, in this order, and the order is the whole point:
//   1. `image` — a picture somebody UPLOADED with the award. A customer inventing its own prize
//      cannot put a PNG in our build, so its picture lives on data.Award.Image as a blob URL.
//   2. our bundled art, by NAME — the 11 files in assets/images/awards for the Hercules catalogue
//      ("FirstPlace", "Fairplay", ...). Still first-class: those prizes have no Image and never
//      need one.
//   3. the generic cup, which is what 34 of our own 45 have always drawn.
//
// It lives here and not in each screen because it used to live in each screen: the participants'
// award page and the public profile each had their own copy of step 2 and neither knew about
// step 1, so an uploaded picture would have shown on one screen and not the other.
export const awardArt = (name, image) => {
  if (image) return image;
  try {
    // The template literal is deliberate: webpack turns it into a context over the whole folder,
    // which is what lets a name decide the file. Same call the three screens each used to hold.
    return require(`./assets/images/awards/${name}.png`);
  } catch {
    return require('./assets/images/fallback/award.png');
  }
};


// Where an album photograph is fetched from, and why it is not simply `photo.url`.
//
// A picture that was TURNED keeps its address: the turned bytes are written back under the SAME
// blob name, deliberately, because that name is the photo's identity — the likes, the tags and
// the comments all hang off the string. So the photograph changes and its URL does not, and a
// browser holding the crooked one would happily go on drawing it. The blob's date is the thing
// that moved, so it is what the address is stamped with.
//
// Takes any photo the album routes hand back ({name, url, uploaded_at}); a payload without a date
// simply gets the plain URL, which is what every screen did before pictures could be turned.
export const photoSrc = (photo) => {
  const version = Date.parse((photo || {}).uploaded_at || '');
  return Number.isFinite(version) ? `${photo.url}?v=${version}` : (photo || {}).url;
};

// ─── Saving and sharing a photograph ─────────────────────────────────────────────────────────
//
// All of this exists because of one browser rule: <a download> is IGNORED when the file lives on
// another origin. Every photograph we show sits in a public blob container on the storage account
// ('albums' for album and team photos, 'images' for the pictures hanging off posts and missions),
// and that is a different origin from tornea.app — so every download button written the obvious way
// quietly did the wrong thing: it opened the picture in a new tab and left the person to long-press
// it. On a phone, which is where an album is actually looked at, that is the difference between a
// photo somebody sends to their team that evening and a photo nobody ever sees again.
//
// So we fetch the bytes ourselves and save a blob: URL instead, which IS same-origin and therefore
// does honour the download attribute. CORS on Azure Storage is set per SERVICE, not per container,
// so one rule covers both of ours: a plain GET is answered `Access-Control-Allow-Origin: *`. Which
// is also why the fetch must never send credentials — '*' together with credentials is a CORS
// failure, and that would break every photo on every screen at once.

// A blob name carries its folder prefix ('20160409/2016-FlairBattles-46599.jpg') and the address we
// render carries photoSrc's ?v= cache-buster. Neither belongs in the name of the file that lands in
// somebody's camera roll.
//
// ★ Decode BEFORE splitting off the folder. That prefix reaches us as a literal slash when a screen
// hands over the blob name, and as %2F when it hands over the address it built with
// encodeURIComponent — Azure serves both, so both turn up. Splitting first leaves the %2F sitting
// inside the file name, and a "name" with a separator still in it is one browsers sanitise however
// they like.
export const photoFileName = (nameOrUrl) => {
  let clean = String(nameOrUrl || '').split('?')[0].split('#')[0];
  try { clean = decodeURIComponent(clean); } catch { /* keep it raw if it isn't valid escaping */ }
  return clean.substring(clean.lastIndexOf('/') + 1) || 'photo.jpg';
};

// Fetch the picture as a File, ready to be saved or handed to the share sheet. Returns null when
// the bytes cannot be had — offline, a blob deleted since the list was drawn, a CORS rule changed
// under us — and every caller then falls back to simply opening the image, which is what these
// buttons did before and still beats a tap that does nothing at all.
const fetchPhotoFile = async (url, fileName) => {
  try {
    const res = await fetch(url); // no credentials — see the note above
    if (!res.ok) return null;
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  } catch {
    return null;
  }
};

const saveFile = (file) => {
  const objectUrl = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // ★ Revoke LATER, not on the next line. The click only schedules the save; tearing the address
  // down synchronously cancels it in Safari, and the button then does nothing on exactly the
  // browser most of our phones run.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
};

// Does this browser offer a native share sheet for FILES? Desktop browsers almost all say no, and
// that is fine — they get the download button on its own. Chrome on Android and Safari on iOS say
// yes, and that is the path that matters: it puts WhatsApp, Instagram and Messages one tap away,
// which is what people actually mean when they say they want to share a photo.
//
// The probe needs a real File, because canShare({files}) is false for anything else — so it hands
// over an empty one rather than sniffing the user agent.
export const canSharePhotos = () => {
  try {
    return !!(navigator.canShare && navigator.share
      && navigator.canShare({ files: [new File([''], 'probe.jpg', { type: 'image/jpeg' })] }));
  } catch {
    return false;
  }
};

// Save one photograph to the device. Returns 'saved', or 'opened' when we could not get the bytes
// and fell back to opening the picture.
export const downloadPhoto = async (url, name) => {
  const fileName = photoFileName(name || url);
  const file = await fetchPhotoFile(url, fileName);
  if (!file) { window.open(url, '_blank', 'noopener'); return 'opened'; }
  saveFile(file);
  return 'saved';
};

// Hand one photograph to the native share sheet, falling back to saving it when that is not on
// offer. Returns 'shared', 'cancelled', 'saved' or 'opened'.
//
// ★ The fetch has to finish BEFORE navigator.share() can be called, and that is the trap: share()
// is only allowed while the browser still counts the tap as recent (transient activation, a few
// seconds). A heavy photo on a slow connection outlives that window and the call throws
// NotAllowedError. An activation cannot be held across an await, so we read that throw as "the
// sheet was not available" and save the file instead — the person still ends up with the picture.
export const sharePhoto = async (url, name, title) => {
  const fileName = photoFileName(name || url);
  const file = await fetchPhotoFile(url, fileName);
  if (!file) { window.open(url, '_blank', 'noopener'); return 'opened'; }

  if (navigator.canShare && navigator.share) {
    try {
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], ...(title ? { title } : {}) });
        return 'shared';
      }
    } catch (err) {
      // ★ AbortError is somebody tapping "Cancel" on the sheet. That is a decision, not a failure,
      // and must NOT be answered by dropping the file into their downloads anyway.
      if (err && err.name === 'AbortError') return 'cancelled';
    }
  }

  saveFile(file);
  return 'saved';
};


// Sponsor walls: turn the flat list the API returns into the groups the page shows.
//
// The API already hands the list back ORDERED — most important sponsor type first,
// company name inside a type — and one row per (company × type), so a sponsor tagged
// with several types legitimately appears more than once. Grouping is therefore just
// "start a new group when type_name changes"; nothing here sorts or ranks, so the
// order shown is the order the back office set in Machina › Admin › Sponsor types and
// every surface (event page, home, storefront, Bravos home) agrees.
//
// A sponsor with no type has type_name null and the API sorts it last; its group keeps
// `type: null`, which the pages render without a heading rather than dropping.
export const groupSponsorsByType = (sponsors) => {
  const groups = [];
  (sponsors || []).forEach((s) => {
    const type = s.type_name || null;
    const last = groups[groups.length - 1];
    if (last && last.type === type) last.items.push(s);
    else groups.push({ type, items: [s] });
  });
  return groups;
};


export const logout = async () => {
  // Make a request to the backend to clear the JWT cookie. The response tells us
  // where to land: the session's BU storefront (tornea.app/<slug>) when it has
  // one, so e.g. a Bravos member returns to tornea.app/bravosuae rather than the
  // bare login and doesn't think the storefront disappeared.
  let redirectTo = null;
  try {
    const res = await fetch('/api/logout', {
      method: 'POST',
      credentials: 'include'  // Ensure cookies are sent with the request
    });
    const data = await res.json().catch(() => ({}));
    redirectTo = data && data.redirect;
  } catch {
    // Network error — fall through to the client-side fallbacks below.
  }

  // Fallback when the server couldn't resolve a slug (e.g. an expired session
  // whose token no longer decodes): the brand slug of the storefront the visitor
  // arrived through, stashed in localStorage by Storefront.js.
  if (!redirectTo) {
    const brandSlug = localStorage.getItem('brandSlug');
    if (brandSlug) redirectTo = `/${brandSlug}`;
  }

  const toastMessage = sessionStorage.getItem('toastMessage');
  // Clear sessionStorage
  sessionStorage.clear();

  if (toastMessage) {
    sessionStorage.setItem('toastMessage', toastMessage);
  }

  // Tell any UI gated on auth (e.g. NavBar) to hide itself.
  localStorage.removeItem('isLoggedIn');
  window.dispatchEvent(new Event('auth-changed'));

  // Redirect to the branded storefront when we have one, else the login page.
  window.location.href = redirectTo || '/login';
};



export const handleImageUpload = async (file, uploadUrl, setError, setShowToast) => {
  const formData = new FormData();
  formData.append('file', file); // Append the image file

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Othercompanyid': sessionStorage.getItem('otherCompanyId'),
        'Otherteamid': sessionStorage.getItem('otherTeamId'),
        'Missionid': sessionStorage.getItem('missionId'),
      },
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      window.location.reload(); // Reload the window after successful upload
      setError(data.message || t('uploadSuccess'));
      setShowToast(true);
    } else {
      const errorData = await response.json();
      throw new Error(errorData.message || t('somethingWentWrong')); // Create an error for the catch block
    }
  } catch (error) {
    setError(error.message); // Set error message in state
    setShowToast(true); // Show the toast notification when there's an error
  }
};


export const handleDeleteImage = async (deleteUrl, setError, setShowToast) => {
      const confirmed = window.confirm(t('areYouSureDeleteImage'));
      if (!confirmed) return;
    
      try {
        await apiRequest('DELETE', deleteUrl);
        setError(null);
        window.location.reload();
      } catch (error) {
        setError(error.message);
        setShowToast(true);
      }
    };


export function applyStoredBrandingColors() {
  const savedPrimaryColor = localStorage.getItem("primaryColor");
  const savedSecondaryColor = localStorage.getItem("secondaryColor");

  if (savedPrimaryColor) {
    document.documentElement.style.setProperty("--primary-color", savedPrimaryColor);
  } else {
    document.documentElement.style.removeProperty("--primary-color");
  }

  if (savedSecondaryColor) {
    document.documentElement.style.setProperty("--secondary-color", savedSecondaryColor);
  } else {
    document.documentElement.style.removeProperty("--secondary-color");
  }
}

export function setFavicon() {
  const savedFavicon = localStorage.getItem("favicon") || `${window.location.origin}/favicon.ico`;

  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
  }
  link.href = savedFavicon;
}

// Swap the PWA manifest (and iOS touch icon) to the per-brand endpoint, so
// "add to home screen" installs under the brand's name and icon.
export function applyBrandManifest(manifestUrl, touchIcon) {
  let link = document.querySelector("link[rel='manifest']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.crossOrigin = 'use-credentials';   // manifest fetch must carry the JWT cookie
  link.href = manifestUrl;
  if (touchIcon) {
    let apple = document.querySelector("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.href = touchIcon;
  }
}

const TEAM_PHOTO_FALLBACK = require('./assets/images/fallback/team.png');

/**
 * The picture to show for a team, in the one order every screen uses: the
 * first candidate that is set (own photo, then company logo — pass them in
 * that order), else the event's own default team picture (branding), else
 * the bundled placeholder. Never hand-roll `photo || teamFallback`: that
 * skipped the event default and showed the wrong picture on half the screens.
 */
export const teamPhoto = (...candidates) => {
  const own = candidates.find(Boolean);
  if (own) return own;
  try {
    const eventDefault = localStorage.getItem('defaultTeamPhoto');
    if (eventDefault) return eventDefault;
  } catch {
    // storage blocked — fall through to the bundled placeholder
  }
  return TEAM_PHOTO_FALLBACK;
};

/**
 * Same, for a screen that lists teams from SEVERAL events (team picker, profile, company
 * page): the browser's default belongs to the event you are logged into, so the server
 * sends each team its own event's default as `default_photo` and that is used instead.
 */
export const teamPhotoOf = (team, ...candidates) =>
  candidates.find(Boolean) || team?.default_photo || TEAM_PHOTO_FALLBACK;

export async function handleBranding() {
  const currentDomain = window.location.hostname;

  try {
      // Visitors who arrived via a brand page (tornea.app/<slug>) keep that
      // brand's look on /login and friends: the storefront stores the slug
      // and /branding resolves the BU's colour + background from it whenever
      // no event session outranks it.
      const brandSlug = localStorage.getItem('brandSlug');
      const branding = await apiRequest(
        'GET',
        brandSlug ? `/branding?slug=${encodeURIComponent(brandSlug)}` : '/branding'
      );

      // Save explicit branding colors only. If absent, let CSS defaults apply.
      if (branding.primary_color) {
        localStorage.setItem("primaryColor", branding.primary_color);
      } else {
        localStorage.removeItem("primaryColor");
      }

      if (branding.secondary_color) {
        localStorage.setItem("secondaryColor", branding.secondary_color);
      } else {
        localStorage.removeItem("secondaryColor");
      }

      localStorage.setItem("favicon", branding.fav_icon || `${window.location.origin}/favicon.ico`);
      if (branding.share_picture) {
        localStorage.setItem("backgroundPicture", branding.share_picture);
      } else {
        localStorage.removeItem("backgroundPicture");
      }

      // Project-level default team/player photos: the fallbacks the team
      // screens show when no picture is set (instead of the bundled ones).
      if (branding.default_team_photo) {
        localStorage.setItem("defaultTeamPhoto", branding.default_team_photo);
      } else {
        localStorage.removeItem("defaultTeamPhoto");
      }
      if (branding.default_player_photo) {
        localStorage.setItem("defaultPlayerPhoto", branding.default_player_photo);
      } else {
        localStorage.removeItem("defaultPlayerPhoto");
      }

      if (branding.is_account_required === true || branding.is_account_required === false) {
        sessionStorage.setItem("isAccountRequired", branding.is_account_required ? '1' : '0');
      } else {
        sessionStorage.removeItem("isAccountRequired");
      }

      localStorage.setItem("brandingDomain", currentDomain);

      // Add-to-home-screen identity: point the PWA manifest at the dynamic
      // per-brand endpoint (cookie rides along via use-credentials) and give
      // iOS its apple-touch-icon. Installing from a PLG session then shows
      // the PLG name + logo instead of Tornea's. The server picks the slug
      // variant for cookieless brand-page visitors.
      applyBrandManifest(branding.manifest_url || '/api/manifest',
                         branding.brand_logo || null);
  } catch (error) {
      console.error("Failed to fetch branding:", error);
  }

  // Always apply best available values (fresh fetch or previously stored).
  applyStoredBrandingColors();
  setFavicon();

  // The `storage` event only fires in OTHER tabs, so AuthShell (which reads
  // backgroundPicture from localStorage at mount) wouldn't see same-tab
  // updates until a refresh. Dispatch a custom event so any mounted component
  // can re-read fresh branding values.
  window.dispatchEvent(new Event('branding-changed'));
}


export const handleSessionRedirect = (navigate, forced = false) => {
  // forced=true means the caller has just confirmed the viewer is authenticated
  // (e.g. /check-auth came back "Authenticated"). Mirror that into the UI flag
  // the NavBar checks so it can show even if the user landed on an auth page
  // while a valid cookie was still in the jar.
  if (forced) {
    localStorage.setItem('isLoggedIn', '1');
    window.dispatchEvent(new Event('auth-changed'));
  }

  const redirectPath = sessionStorage.getItem('redirect');

  if (redirectPath || forced) {
    sessionStorage.removeItem('redirect');
    navigate(redirectPath || '/home');
  }
};

export const formatRelativeTime = (dateInput, locale = undefined) => {
  if (!dateInput) return '';

  const date = new Date(dateInput);
  const now = new Date();

  const diffMs = date.getTime() - now.getTime(); // negative = past
  const diffSec = Math.round(diffMs / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const absSec = Math.abs(diffSec);

  if (absSec < 60) return rtf.format(diffSec, 'second');

  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');

  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');

  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 7) return rtf.format(diffDay, 'day');

  const diffWeek = Math.round(diffDay / 7);
  if (Math.abs(diffWeek) < 5) return rtf.format(diffWeek, 'week');

  const diffMonth = Math.round(diffDay / 30);
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month');

  const diffYear = Math.round(diffDay / 365);
  return rtf.format(diffYear, 'year');
};

